"use client";

import Link from "@/infrastructure/router/Link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import { CommunityThread as PersistedCommunityThread } from "@/components/community/CommunityThread";
import { TrackEditView } from "@/components/make/TrackEditView";
import {
	ArtistIcon,
	Avatar,
	Callout,
	EmptyState,
	GlassCard,
	GlassIconButton,
	GlassTextField,
	MessageBubble,
	MessageInputBar,
	NavigationBar,
	Skeleton,
	Toast,
} from "@/components/design-system/ArtistDesignSystem";
import {
	type Conversation,
	type Message,
	offlineSocialRepository,
	type SocialRepository,
	sameScope,
	scopeKey,
} from "@/domain/social/SocialRepository";
import { createLocalNote } from "@/domain/social/LocalNoteStore";

const DRAFTS_KEY = "artist.social-drafts.v1";
const publicAvatar =
	"https://crlauxhyezjqwdfetukb.supabase.co/storage/v1/object/public/avatars/3db5dc21-18d7-45eb-b238-c729ad0df713/avatar_362cbc2f-56e7-4e0e-b26f-a72313c0d4e9.jpg";

type DraftMap = Record<string, unknown>;
const graphemeSegmenter = new Intl.Segmenter(undefined, {
	granularity: "grapheme",
});

function readDraft<T>(key: string, fallback: T): T {
	if (typeof window === "undefined") return fallback;
	try {
		const record = JSON.parse(
			window.localStorage.getItem(DRAFTS_KEY) ?? "{}",
		) as DraftMap;
		return key in record ? (record[key] as T) : fallback;
	} catch {
		return fallback;
	}
}

function writeDraft(key: string, value: unknown): void {
	try {
		const record = JSON.parse(
			window.localStorage.getItem(DRAFTS_KEY) ?? "{}",
		) as DraftMap;
		record[key] = value;
		window.localStorage.setItem(DRAFTS_KEY, JSON.stringify(record));
	} catch {
		// The mounted state remains authoritative when persistent storage is unavailable.
	}
}

function clearDraft(key: string): void {
	try {
		const record = JSON.parse(
			window.localStorage.getItem(DRAFTS_KEY) ?? "{}",
		) as DraftMap;
		delete record[key];
		window.localStorage.setItem(DRAFTS_KEY, JSON.stringify(record));
	} catch {
		// No visible state is derived from a failed cleanup.
	}
}

export function graphemeCount(value: string): number {
	return Array.from(graphemeSegmenter.segment(value)).length;
}

export function boundedText(
	value: string,
	maxGraphemes: number,
	maxLines = Number.MAX_SAFE_INTEGER,
): string {
	const lines = value.split("\n").slice(0, maxLines).join("\n");
	return Array.from(graphemeSegmenter.segment(lines), ({ segment }) => segment)
		.slice(0, maxGraphemes)
		.join("");
}

// Re-exported from the dedicated EditProfile module so existing imports
// (`import { ProfileEditHub } from "@/components/social/SocialHubs"`) keep
// resolving — the implementation now lives in src/components/profile/.
export { ProfileEditHub } from "@/components/profile/EditProfileView";

function useScope(): {
	readonly scope: ReturnType<typeof useArtistSession>["scope"];
	readonly ready: boolean;
} {
	const auth = useArtistSession();
	return { scope: auth.scope, ready: auth.phase === "user" };
}

function ScopeGate({ children }: Readonly<{ children: React.ReactNode }>) {
	const { ready, scope } = useScope();
	if (ready && scope !== null)
		return <ScopedSurface key={scopeKey(scope)}>{children}</ScopedSurface>;
	return (
		<section className="social-gate">
			<NavigationBar title="Artist" />
			<EmptyState
				title="登录后继续"
				message="你的草稿、资料和会话会保存在自己的账户中。"
				symbol={<ArtistIcon name="person" size={40} />}
			/>
			<Link className="primary-action" href="/auth">
				登录
			</Link>
		</section>
	);
}

function ScopedSurface({ children }: Readonly<{ children: React.ReactNode }>) {
	return <>{children}</>;
}

function useFence() {
	const scope = useArtistSession().scope;
	const current = useRef(scope);
	const generations = useRef(new Map<string, number>());
	useEffect(() => {
		current.current = scope;
		generations.current.clear();
		return () => {
			current.current = null;
			generations.current.clear();
		};
	}, [scope]);
	return useCallback((requestKey: string) => {
		const expectedScope = current.current;
		const expectedGeneration = (generations.current.get(requestKey) ?? 0) + 1;
		generations.current.set(requestKey, expectedGeneration);
		return () =>
			sameScope(current.current, expectedScope) &&
			generations.current.get(requestKey) === expectedGeneration;
	}, []);
}

function Notice({
	children,
	tone = "info",
}: Readonly<{
	children: React.ReactNode;
	tone?: "info" | "error" | "success";
}>) {
	return <Toast tone={tone}>{children}</Toast>;
}

export function NoteComposeHub() {
	return (
		<ScopeGate>
			<NoteComposer />
		</ScopeGate>
	);
}

type NoteComposeImage = {
	readonly id: string;
	readonly url: string;
	readonly name: string;
	readonly file: File;
};

function NoteComposer() {
	const { scope } = useScope();
	const [body, setBody] = useState("");
	const [images, setImages] = useState<NoteComposeImage[]>([]);
	const [savedBody, setSavedBody] = useState("");
	const [notice, setNotice] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [showVisibility, setShowVisibility] = useState(false);
	const [isPublishing, setIsPublishing] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const visibilityDialogRef = useRef<HTMLDialogElement>(null);
	const imagesRef = useRef<NoteComposeImage[]>([]);
	imagesRef.current = images;

	useEffect(() => {
		if (!scope) return;
		const saved = readDraft<{ body: string }>(
			`note:${scopeKey(scope)}`,
			{ body: "" },
		);
		setBody(saved.body);
		setImages([]);
		setSavedBody(
			readDraft<{ body: string }>(`note:saved:${scopeKey(scope)}`, { body: "" })
				.body,
		);
	}, [scope]);

	useEffect(() => {
		return () => {
			imagesRef.current.forEach((img) => {
				URL.revokeObjectURL(img.url);
			});
		};
	}, []);

	useEffect(() => {
		const dialog = visibilityDialogRef.current;
		if (dialog === null) return;
		if (showVisibility) {
			if (!dialog.open && typeof dialog.showModal === "function")
				dialog.showModal();
		} else if (dialog.open) {
			dialog.close();
		}
	}, [showVisibility]);

	if (!scope) return <SocialLoading title="正在载入笔记…" />;
	const activeScope = scope;
	const valid = body.trim().length > 0;
	const lineCount = body === "" ? 0 : body.split("\n").length;

	const update = (next: string) => {
		const safe = boundedText(next, 100, 10);
		setBody(safe);
		writeDraft(`note:${scopeKey(activeScope)}`, { body: safe });
	};

	const handleSelectFiles = (files: FileList | null) => {
		if (files === null) return;
		const room = Math.max(0, 9 - images.length);
		const next: NoteComposeImage[] = [];
		Array.from(files)
			.slice(0, room)
			.forEach((file) => {
				if (!file.type.startsWith("image/")) return;
				next.push({
					id: crypto.randomUUID(),
					url: URL.createObjectURL(file),
					name: file.name,
					file,
				});
			});
		if (next.length === 0) return;
		const merged = [...images, ...next].slice(0, 9);
		setImages(merged);
		writeDraft(`note:${scopeKey(activeScope)}`, { body });
	};

	const removeImage = (id: string) => {
		const removed = images.find((img) => img.id === id);
		if (removed) URL.revokeObjectURL(removed.url);
		const next = images.filter((img) => img.id !== id);
		setImages(next);
		writeDraft(`note:${scopeKey(activeScope)}`, { body });
	};

	const presentVisibility = () => {
		if (!valid || isPublishing) return;
		setError(null);
		setShowVisibility(true);
	};

	const publish = async (visibility: "public" | "private") => {
		setShowVisibility(false);
		setIsPublishing(true);
		setError(null);
		try {
			const profile = await offlineSocialRepository.loadProfile(activeScope);
			await createLocalNote(activeScope, {
				body,
				visibility,
				authorName: profile.value.displayName,
				mediaFiles: images.map((image) => image.file),
			});
			clearDraft(`note:${scopeKey(activeScope)}`);
			setSavedBody(body);
			setNotice(visibility === "public" ? "笔记已发布。" : "私密笔记已保存。");
			images.forEach((img) => {
				URL.revokeObjectURL(img.url);
			});
			setBody("");
			setImages([]);
		} catch {
			setError("笔记保存失败，请检查浏览器存储权限后重试。");
		} finally {
			setIsPublishing(false);
		}
	};

	return (
		<section className="social-page note-compose-page" aria-label="写灵感">
			<NavigationBar
				title="写灵感"
				backHref="/community"
				leading={
					<Link className="ds-icon-button" href="/community" aria-label="关闭">
						<ArtistIcon name="close" size={24} strokeWidth={2} />
					</Link>
				}
				trailing={
					<button
						type="button"
						className="note-compose-publish"
						aria-label="下一步"
						disabled={!valid || isPublishing}
						onClick={presentVisibility}
					>
						{isPublishing ? (
							<span className="ds-spinner" aria-hidden="true" />
						) : (
							<ArtistIcon name="send" size={18} />
						)}
					</button>
				}
			/>
			{error ? (
				<Callout
					style="error"
					title="发布失败"
					message={error}
					actionLabel="知道了"
					onAction={() => setError(null)}
				/>
			) : null}
			{notice ? <Notice tone="success">{notice}</Notice> : null}

			<div className="note-compose-scroll">
				<GlassCard
					className="note-thoughts-card"
					cornerRadius={20}
					style={{ padding: 0 }}
				>
					<div className="note-thoughts-card__quote" aria-hidden="true">
						<ArtistIcon
							name="doc"
							size={28}
							strokeWidth={1.4}
							style={{
								color:
									"color-mix(in display-p3, var(--ds-text-secondary) 35%, transparent)",
							}}
						/>
					</div>
					<div className="note-thoughts-card__editor">
						{body === "" ? (
							<h2 className="note-thoughts-card__title">写下此刻的灵感</h2>
						) : null}
						<label className="note-thoughts-card__field">
							<span className="sr-only">笔记正文</span>
							<textarea
								aria-label="笔记正文"
								value={body}
								onChange={(event) => update(event.currentTarget.value)}
								placeholder={
									body === "" ? "一行字、一段旋律、一个采风片段…" : ""
								}
								rows={6}
							/>
						</label>
					</div>
					<div className="note-thoughts-card__counter">
						<span>
							{graphemeCount(body)}/100 · {lineCount}/10
						</span>
					</div>
				</GlassCard>

				<div className="note-long-article">
					<GlassCard
						className="note-long-article__picker"
						cornerRadius={16}
						style={{ padding: 0 }}
					>
						<button
							type="button"
							className="note-long-article__row"
							onClick={() => fileInputRef.current?.click()}
							aria-label="添加图片"
						>
							<span className="note-long-article__icon" aria-hidden="true">
								<ArtistIcon name="image" size={22} />
							</span>
							<span className="note-long-article__copy">
								<strong>写长文</strong>
								<small>添加图片，最多 9 张</small>
							</span>
							<span className="note-long-article__trailing" aria-hidden="true">
								<ArtistIcon name="chevron" size={18} />
							</span>
						</button>
						<input
							ref={fileInputRef}
							type="file"
							accept="image/*"
							multiple
							className="sr-only"
							onChange={(event) => {
								handleSelectFiles(event.currentTarget.files);
								event.currentTarget.value = "";
							}}
						/>
					</GlassCard>

					{images.length > 0 ? (
						<GlassCard
							className="note-long-article__grid-card"
							cornerRadius={16}
						>
							<ul className="note-image-grid">
								{images.map((img) => (
									<li key={img.id} className="note-image-grid__item">
									{/* biome-ignore lint/performance/noImgElement: note image preview with object-URL blob; next/image not feasible on static export */}
									<img src={img.url} alt={img.name} />
										<button
											type="button"
											className="note-image-grid__remove"
											aria-label="移除图片"
											onClick={() => removeImage(img.id)}
										>
											<ArtistIcon name="close" size={14} strokeWidth={2.4} />
										</button>
									</li>
								))}
							</ul>
						</GlassCard>
					) : null}
				</div>

				{savedBody ? (
					<GlassCard className="social-saved-note" aria-label="最近保存的笔记">
						<strong>最近保存</strong>
						<p>{savedBody}</p>
					</GlassCard>
				) : null}
			</div>

			{/* biome-ignore lint/a11y/useKeyWithClickEvents: native dialog onCancel handles Escape dismissal; onClick only closes on backdrop click */}
			<dialog
				ref={visibilityDialogRef}
				className="note-visibility-dialog"
				aria-labelledby="note-visibility-title"
				onCancel={(event) => {
					event.preventDefault();
					setShowVisibility(false);
				}}
				onClick={(event) => {
					if (event.target === event.currentTarget) setShowVisibility(false);
				}}
			>
				<div className="note-visibility-dialog__body">
					<h2 id="note-visibility-title">选择笔记可见范围</h2>
					<button
						type="button"
						className="note-visibility-option"
						onClick={() => publish("public")}
					>
						<ArtistIcon name="globe" size={20} />
						<span>
							<strong>公开</strong>
							<small>所有人可在笔记广场查看</small>
						</span>
					</button>
					<button
						type="button"
						className="note-visibility-option"
						onClick={() => publish("private")}
					>
						<ArtistIcon name="shield" size={20} />
						<span>
							<strong>仅自己</strong>
							<small>保存在此设备的草稿中</small>
						</span>
					</button>
					<button
						type="button"
						className="note-visibility-cancel"
						onClick={() => setShowVisibility(false)}
					>
						取消
					</button>
				</div>
			</dialog>
		</section>
	);
}

export function TrackEditHub(
	_props: Readonly<{ repository?: SocialRepository }> = {},
) {
	return <TrackEditView />;
}

export function InboxHub({
	repository = offlineSocialRepository,
}: Readonly<{ repository?: SocialRepository }>) {
	return (
		<ScopeGate>
			<Inbox repository={repository} />
		</ScopeGate>
	);
}

function Inbox({ repository }: Readonly<{ repository: SocialRepository }>) {
	const { scope } = useScope();
	const fence = useFence();
	const [rows, setRows] = useState<readonly Conversation[] | null>(null);
	const [query, setQuery] = useState("");
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		if (!scope) return;
		const applies = fence("inbox.load");
		setRows(null);
		setError(null);
		repository
			.loadInbox(scope)
			.then((value) => applies() && setRows(value))
			.catch(() => applies() && setError("消息加载失败，请稍后重试。"));
	}, [fence, repository, scope]);
	if (!scope || rows === null) return <SocialLoading title="正在载入消息…" />;
	const filtered = rows.filter((row) =>
		row.participants.join(" ").toLowerCase().includes(query.toLowerCase()),
	);
	return (
		<section className="social-page inbox-page" aria-label="消息">
			<NavigationBar
				title="消息"
				trailing={
					<GlassIconButton label="新建会话" disabled>
						<ArtistIcon name="plus" />
					</GlassIconButton>
				}
			/>
			{error ? <Notice tone="error">{error}</Notice> : null}
			<GlassTextField
				className="inbox-search"
				label="搜索会话"
				leading={<ArtistIcon name="search" />}
				value={query}
				onChange={(event) => setQuery(event.currentTarget.value)}
			/>
			<GlassCard className="inbox-list">
				{filtered.length === 0 ? (
					<EmptyState
						title="没有会话"
						message="尝试其他搜索内容。"
						symbol={<ArtistIcon name="message" size={40} />}
					/>
				) : (
					filtered.map((row) => <ConversationRow key={row.id} row={row} />)
				)}
			</GlassCard>
		</section>
	);
}

function ConversationRow({ row }: Readonly<{ row: Conversation }>) {
	const title =
		row.participants.filter((name) => name !== "你").join("、") || "未知用户";
	return (
		<Link className="conversation-row" href={`/messages/thread/${row.id}`}>
			<span className="conversation-avatar-wrap">
				<Avatar name={title} src={publicAvatar} />
				{row.unread > 0 ? <i /> : null}
			</span>
			<span>
				<strong>{title}</strong>
				<small>{row.snippet}</small>
			</span>
			<time>
				{row.at}
				{row.unread > 0 ? <b>{row.unread}</b> : null}
			</time>
		</Link>
	);
}

export function MessageThreadHub({
	id,
	repository = offlineSocialRepository,
}: Readonly<{ id: string; repository?: SocialRepository }>) {
	return (
		<ScopeGate>
			<MessageThread id={id} repository={repository} />
		</ScopeGate>
	);
}

function MessageThread({
	id,
	repository,
}: Readonly<{ id: string; repository: SocialRepository }>) {
	const { scope } = useScope();
	const fence = useFence();
	const [messages, setMessages] = useState<readonly Message[] | null>(null);
	const [draft, setDraft] = useState("");
	const [notice, setNotice] = useState<string | null>(null);
	useEffect(() => {
		if (!scope) return;
		const applies = fence(`messages.load:${id}`);
		setMessages(null);
		setNotice(null);
		setDraft(readDraft(`message:${scopeKey(scope)}:${id}`, ""));
		Promise.all([
			repository.loadMessages(scope, id),
			repository.markRead(scope, id),
		])
			.then(
				([loaded]) =>
					applies() && setMessages([...loaded].toSorted((a, b) => a.at - b.at)),
			)
			.catch(() => applies() && setNotice("消息记录加载失败，请稍后重试。"));
	}, [fence, id, repository, scope]);
	if (!scope || messages === null)
		return <SocialLoading title="正在载入会话…" />;
	const activeScope = scope;
	const currentMessages = messages;
	async function send(clientId?: string, retryBody?: string): Promise<void> {
		const body = (retryBody ?? draft).trim();
		if (!body) return;
		const localId = clientId ?? crypto.randomUUID();
		const existing = currentMessages.find(
			(message) => message.clientId === localId,
		);
		const optimistic: Message = existing ?? {
			id: `local-${localId}`,
			clientId: localId,
			body,
			author: "me",
			at: Date.now(),
			state: "failed",
		};
		if (!existing)
			setMessages((current) =>
				current?.some((message) => message.clientId === localId)
					? current
					: [...(current ?? []), optimistic].toSorted((a, b) => a.at - b.at),
			);
		setDraft("");
		clearDraft(`message:${scopeKey(activeScope)}:${id}`);
		const applies = fence(`message.send:${localId}`);
		try {
			const result = await repository.sendMessage(activeScope, id, optimistic);
			if (!applies()) return;
			setMessages((current) =>
				(current ?? []).map((message) =>
					message.clientId === localId
						? { ...message, state: result.kind === "sent" ? "sent" : "failed" }
						: message,
				),
			);
			setNotice(result.kind === "sent" ? null : "消息未发送，点按重试。");
		} catch {
			if (applies()) setNotice("消息未发送，点按重试。");
		}
	}
	return (
		<section className="social-page message-thread" aria-label="夜航工作室">
			<NavigationBar title="夜航工作室" backHref="/messages/inbox" />
			{notice ? <Notice tone="error">{notice}</Notice> : null}
			<div className="message-list">
				{messages.map((message) => (
					<MessageBubble
						key={message.clientId}
						mine={message.author === "me"}
						meta={message.state === "failed" ? "发送失败" : "已发送"}
						failedAction={
							message.state === "failed" ? (
								<button
									type="button"
									onClick={() => void send(message.clientId, message.body)}
								>
									重试
								</button>
							) : undefined
						}
					>
						{message.body}
					</MessageBubble>
				))}
			</div>
			<MessageInputBar
				label="消息正文"
				value={draft}
				onChange={(value) => {
					setDraft(value);
					writeDraft(`message:${scopeKey(activeScope)}:${id}`, value);
				}}
				onSend={() => void send()}
				disabled={!draft.trim()}
			/>
		</section>
	);
}

export function CommunityThreadHub({
	id,
	repository = offlineSocialRepository,
}: Readonly<{ id: string; repository?: SocialRepository }>) {
	return <PersistedCommunityThread id={id} repository={repository} />;
}

function SocialLoading({ title }: Readonly<{ title: string }>) {
	return (
		<section className="social-loading" aria-busy="true">
			<NavigationBar title="Artist" />
			<Skeleton rows={4} />
			<p className="sr-only">{title}</p>
		</section>
	);
}
